import usePageTitle from "../../core/hooks/UsePageTitle";
import DefaultLayout from "../layouts/default/Default.layout";
import { Form, Tabs, notification, Spin, Modal, Tooltip } from "antd";
import { Alternativa, Resolucao, Usuario } from "../../sdk/@types";
import { PdfHighlight } from "../../sdk/@types/Acertei";
import React, { useEffect, useLayoutEffect, useReducer, useRef, useState } from "react";
import useResolucaoQuestoes from "../../core/hooks/UseResolucaoQuestoes";
import ResolucaoService from "../../sdk/services/ResolucaoService";
import useUsuario from "../../core/hooks/UseUsuario";
import QuestaoService from "../../sdk/services/QuestaoService";
import PdfHighlightService from "../../sdk/services/PdfHighlightService";
import MarkdownEditor from "../components/MarkdownEditor";
import { useNavigate } from "react-router-dom";
import { usePageBlockStore } from "../../core/store/usePageBlockStore";
import { ChoiceButton } from "../components/ChoiceButton/ChoiceButton";
import PdfViewer from "../components/PdfViewer/PdfViewer";
import PdfAnnotator from "../components/PdfAnnotator/PdfAnnotator";
import ReviewService from "../../sdk/services/ReviewService";
import SharedTextPanel from "../components/SharedTextPanel/SharedTextPanel";
import { PanelGeometry } from "../../core/hooks/useDraggable";
import QuestionActionBar from "../components/QuestionActionBar/QuestionActionBar";
import QuestionForum from "../components/Forum/QuestionForum";
import ReportQuestionModal from "../components/Forum/ReportQuestionModal";
import type { QuestionReportReference } from "../components/Forum/ReportQuestionModal";
import type { Profile } from "../auth/accessPolicy";
import { parseEnunciado, TAG_TINTS, ScissorButton } from "./carrossel/questionCard";
import confetti from "canvas-confetti";
import {
  Ban,
  BookOpenText,
  Brain,
  Check,
  ChevronRight,
  CircleCheck,
  Eye,
  FileText,
  Flag,
  History,
  Landmark,
  Lightbulb,
  Loader2,
  PanelRightClose,
  PanelRightOpen,
  Pencil,
  Scissors,
  TriangleAlert,
} from "lucide-react";

/** Id de resolução ainda não persistida — enquanto for este, a questão aceita escolha. */
const RESOLUCAO_VAZIA = "000000000000000000000000";

const cx = (...v: Array<string | false | null | undefined>) => v.filter(Boolean).join(" ");

/** Linha de ferramenta do painel lateral (design: "Ferramentas"). */
function ToolRow({
  icon,
  tile,
  title,
  desc,
  onClick,
  disabled = false,
  loading = false,
}: {
  icon: React.ReactNode;
  /** Classes do quadradinho do ícone. */
  tile: string;
  title: string;
  desc: string;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={cx(
        "group flex w-full items-center gap-3 rounded-chip p-1 text-left",
        "transition-colors duration-150 motion-reduce:transition-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60",
        disabled ? "cursor-not-allowed opacity-50" : "hover:bg-surface-hover",
      )}
    >
      <span
        className={cx("flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px]", tile)}
      >
        {loading ? <Loader2 size={18} className="animate-spin" aria-hidden /> : icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-ink">{title}</span>
        <span className="block truncate text-xs text-ink-secondary">{desc}</span>
      </span>
      <ChevronRight
        size={16}
        className="shrink-0 text-ink-muted transition-transform group-hover:translate-x-0.5"
        aria-hidden
      />
    </button>
  );
}

export default function CarrosselForm() {
  const [form] = Form.useForm<Resolucao.ResolucaoQuestao>();

  usePageTitle("Carrossel de Questões");

  const { TabPane } = Tabs;

  const [reducerValue] = useReducer((x) => x + 1, 0);
  const navigate = useNavigate();

  useLayoutEffect(() => {
    usePageBlockStore.getState().setBlocking(false);
  }, []);

  const [alternativaEscolhida, setAlternativa] = useState<Alternativa.Alternativa>();
  const [activeTab, setActiveTab] = useState<string>();
  const { usuario } = useUsuario();
  const isAdmin = usuario?.profile === "administrador" || usuario?.profile === "master";

  const [isMobile, setIsMobile] = useState(
    window.innerWidth < 768 || window.innerHeight < 768,
  );
  const [swiping, setSwiping] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768 || window.innerHeight < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [reducerValue, alternativaEscolhida]);

  const {
    resolucoes,
    resolucaoAtual,
    resolucaoAnterior,
    resolucaoProxima,
    paginatedResult,
    filterQuery,
    buscarPaginada,
    proximaPaginacao,
    paginacaoAnterior,
    marcarAcaoNasResolucoes,
    marcarResolucaoAtual,
    fetching,
  } = useResolucaoQuestoes();

  const [loadingAnswer, setLoadingAnswer] = useState(false);
  const [highlight, setHighlight] = useState<PdfHighlight | null>(null);
  const [allPdfMarks, setAllPdfMarks] = useState<PdfHighlight[]>([]);
  const [annotatorOpen, setAnnotatorOpen] = useState(false);
  // Denúncia de questão. Guarda a referência de quem abriu o formulário, e não
  // só um booleano, porque o modal vive fora das abas: a questão atual pode
  // mudar (seta, swipe) com ele aberto, e o aluno estaria denunciando outra.
  const [denuncia, setDenuncia] = useState<{
    id: string;
    reference: QuestionReportReference;
  } | null>(null);
  const [pdfVisible, setPdfVisible] = useState(false);
  const [memorizedIds, setMemorizedIds] = useState<Set<string>>(new Set());
  const [memorizingId, setMemorizingId] = useState<string | null>(null);

  // Texto base aberto no painel flutuante (id do texto, não da questão).
  const [textoAberto, setTextoAberto] = useState<string | null>(null);
  // Grupos cujo texto o aluno já abriu nesta sessão — o botão pulsa até lá.
  const [textosVistos, setTextosVistos] = useState<Set<string>>(new Set());
  // Posição/tamanho sobrevivem ao fechar, então o painel reabre onde ficou.
  const textoGeometriaRef = useRef<PanelGeometry | null>(null);

  // Tesoura — marcações por questão, só desta sessão: nada vai ao banco nem
  // entra nas estatísticas; sobrevivem ao "Anterior"/"Próxima" porque vivem
  // aqui, fora das abas.
  const [eliminadas, setEliminadas] = useState<Record<string, number[]>>({});
  const [riscados, setRiscados] = useState<Record<string, number[]>>({});

  const sharedTexts = paginatedResult?.sharedTexts;
  const grupoAtual = resolucaoAtual.question?.sharedTextId || null;

  /**
   * O painel acompanha o grupo: continua aberto enquanto o aluno percorre as
   * questões do mesmo texto e fecha sozinho ao sair dele.
   */
  useEffect(() => {
    setTextoAberto((prev) => (prev && prev === grupoAtual ? prev : null));
  }, [grupoAtual]);

  const abrirTexto = (sharedTextId: string) => {
    setTextosVistos((prev) => new Set(prev).add(sharedTextId));
    setTextoAberto((prev) => (prev === sharedTextId ? null : sharedTextId));
  };

  /** Abre o formulário de denúncia com a referência da questão de onde saiu. */
  const denunciarQuestao = (q: any, numero: number) => {
    const id = String(q?.id ?? "");
    if (!id) return;
    setDenuncia({
      id,
      reference: {
        number: numero,
        agency: q?.testingAgency?.name,
        year: q?.testingAgency?.year,
        subject: q?.subject?.name,
      },
    });
  };

  const deriveSubjectId = (q?: any): string => {
    if (!q) return "";
    if (q.subject?.id) return String(q.subject.id);
    if (typeof q.subjectId === "string") return q.subjectId;
    if (q.subjectId?.id) return String(q.subjectId.id);
    return "";
  };

  const onMemorize = async (q?: any) => {
    const qid = String(q?.id ?? "");
    if (!qid || memorizedIds.has(qid)) return;
    try {
      setMemorizingId(qid);
      await ReviewService.createCard({
        source: "question",
        questionId: qid,
        subjectId: deriveSubjectId(q),
        tags: (q?.keyword ?? "")
          .split(",")
          .map((t: string) => t.trim())
          .filter(Boolean),
      });
      setMemorizedIds((prev) => new Set(prev).add(qid));
      notification.success({ message: "Adicionada à revisão" });
    } catch (error) {
      if (
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        (error as any).response?.status === 409
      ) {
        setMemorizedIds((prev) => new Set(prev).add(qid));
        notification.info({ message: "Esta questão já está na sua revisão." });
      } else {
        notification.error({
          message: "Não foi possível adicionar à revisão.",
        });
      }
    } finally {
      setMemorizingId(null);
    }
  };

  const questionId = String(resolucaoAtual.question?.id ?? "");

  useEffect(() => {
    if (!questionId || questionId === "" || questionId === "undefined") {
      setHighlight(null);
      setPdfVisible(false);
      return;
    }
    PdfHighlightService.getByQuestion(questionId)
      .then((h) => {
        setHighlight(h ?? null);
        setPdfVisible(false);
        if (h?.pdf_doc_id) {
          PdfHighlightService.getByPdf(h.pdf_doc_id)
            .then((all) =>
              setAllPdfMarks(all.filter((m) => m.highlight_id !== h.highlight_id)),
            )
            .catch(() => setAllPdfMarks([]));
        } else {
          setAllPdfMarks([]);
        }
      })
      .catch(() => {
        setHighlight(null);
        setPdfVisible(false);
        setAllPdfMarks([]);
      });
  }, [questionId]);

  useEffect(() => {
    setActiveTab(resolucaoAtual.question?.id + "");
  }, [
    buscarPaginada,
    resolucaoAtual,
    resolucaoAnterior,
    resolucaoProxima,
    resolucoes,
    alternativaEscolhida,
  ]);

  const irParaProxima = () => {
    if (resolucaoProxima?.question) {
      marcarResolucaoAtual(resolucaoProxima);
    } else {
      proximaPaginacao();
    }
  };

  const irParaAnterior = () => {
    if (resolucaoAnterior?.question) {
      marcarResolucaoAtual(resolucaoAnterior);
    } else {
      paginacaoAnterior();
    }
  };

  /** Marca uma alternativa (clique ou tecla A–E). Só vale antes de responder. */
  const escolher = (
    alternativa: Alternativa.Alternativa,
    resolucao: Resolucao.ResolucaoQuestao,
  ) => {
    if (resolucao.id !== RESOLUCAO_VAZIA) return;

    let resultado = form.getFieldValue("acertou");
    setAlternativa(alternativa);
    resultado = resolucao.question;
    form.setFieldsValue({ question: resultado });

    const resolucaoAlterada = { ...resolucao, answer: alternativa };
    marcarResolucaoAtual(resolucaoAlterada);
    marcarAcaoNasResolucoes(resolucaoAlterada);
    form.resetFields();
  };

  const limparSelecao = (resolucao: Resolucao.ResolucaoQuestao) => {
    const resolucaoAlterada = { ...resolucao, answer: undefined };
    marcarResolucaoAtual(resolucaoAlterada);
    marcarAcaoNasResolucoes(resolucaoAlterada);
  };

  const toggleEliminada = (
    resolucao: Resolucao.ResolucaoQuestao,
    altIndex: number,
  ) => {
    const qid = String(resolucao.question?.id ?? "");
    if (!qid) return;
    const choices =
      resolucao.question?.choices?.filter((c) => c.description?.trim()) ?? [];
    const atual = eliminadas[qid] ?? [];

    if (atual.includes(altIndex)) {
      setEliminadas((prev) => ({
        ...prev,
        [qid]: (prev[qid] ?? []).filter((i) => i !== altIndex),
      }));
      return;
    }

    // Sempre sobra pelo menos uma alternativa.
    if (atual.length >= choices.length - 1) return;

    // Uma alternativa eliminada não pode ficar marcada.
    if (resolucao.answer?.description === choices[altIndex]?.description) {
      limparSelecao(resolucao);
    }

    setEliminadas((prev) => ({
      ...prev,
      [qid]: [...(prev[qid] ?? []), altIndex],
    }));
  };

  const toggleRiscado = (qid: string, itemIndex: number) => {
    if (!qid) return;
    setRiscados((prev) => {
      const atual = prev[qid] ?? [];
      return {
        ...prev,
        [qid]: atual.includes(itemIndex)
          ? atual.filter((i) => i !== itemIndex)
          : [...atual, itemIndex],
      };
    });
  };

  const restaurarTudo = (qid: string) => {
    setEliminadas((prev) => ({ ...prev, [qid]: [] }));
    setRiscados((prev) => ({ ...prev, [qid]: [] }));
  };

  const lerMensagemDeErro = (error: unknown): string => {
    if (
      typeof error === "object" &&
      error !== null &&
      "response" in error &&
      (error as any).response?.data?.error
    ) {
      return (error as any).response.data.error;
    }
    if (typeof error === "object" && error !== null && "message" in error) {
      return (error as any).message;
    }
    return "";
  };

  /**
   * Confete curto no acerto. `disableForReducedMotion` respeita a preferência
   * de movimento do sistema; z-index 950 fica acima da barra mobile (900) e
   * abaixo dos overlays do antd (1000).
   */
  const celebrar = () => {
    const cores = ["#2f6cd2", "#5b8def", "#2d9e6e", "#82ca9d", "#a3e635"];
    const base = { colors: cores, disableForReducedMotion: true, zIndex: 950 };
    confetti({
      ...base,
      particleCount: 80,
      spread: 70,
      startVelocity: 42,
      ticks: 150,
      origin: { y: 0.7 },
    });
    setTimeout(() => {
      confetti({
        ...base,
        particleCount: 30,
        angle: 60,
        spread: 55,
        ticks: 120,
        origin: { x: 0, y: 0.85 },
      });
      confetti({
        ...base,
        particleCount: 30,
        angle: 120,
        spread: 55,
        ticks: 120,
        origin: { x: 1, y: 0.85 },
      });
    }, 150);
  };

  const confirmar = async () => {
    try {
      setLoadingAnswer(true);
      const usu: Usuario.Usuario | undefined = usuario;
      const resolucaoAConfirmar = { ...resolucaoAtual, usuario: usu };

      const resolucaoRespondida =
        await ResolucaoService.confirmarResposta(resolucaoAConfirmar);
      marcarResolucaoAtual(resolucaoRespondida);

      if (resolucaoRespondida.correct) celebrar();

      notification.success({ message: "Ok", description: "Resposta registrada." });
    } catch (error) {
      notification.error({ message: lerMensagemDeErro(error) });
    } finally {
      setLoadingAnswer(false);
    }
  };

  const verGabarito = async () => {
    try {
      setLoadingAnswer(true);
      const usu: Usuario.Usuario | undefined = usuario;
      const resolucaoAConfirmar = { ...resolucaoAtual, usuario: usu };

      const resolucaoRespondida =
        await ResolucaoService.exibirResposta(resolucaoAConfirmar);
      marcarResolucaoAtual(resolucaoRespondida);

      notification.info({
        message: "Informação",
        description: "Verificação registrada.",
      });
    } catch (error) {
      notification.error({ message: lerMensagemDeErro(error) });
    } finally {
      setLoadingAnswer(false);
    }
  };

  const alternarAtivacao = async (q?: any) => {
    try {
      const id = String(q?.id ?? "");
      if (!id) return;

      await QuestaoService.setQuestaoDisabled(id, !Boolean(q?.disabled));
      notification.success({
        message: "Sucesso",
        description: !Boolean(q?.disabled)
          ? "Questão desativada. Ela não aparecerá para usuários que não sejam master/administrador."
          : "Questão ativada.",
      });
      buscarPaginada();
    } catch (e) {
      notification.error({
        message: "Erro",
        description: "Não foi possível ativar/desativar a questão.",
      });
    }
  };

  /** O foco está num campo de texto — as teclas são dele, não do carrossel. */
  const estaDigitando = (target: HTMLElement | null) =>
    Boolean(
      target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable),
    );

  // ArrowKey navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // O painel do texto base também usa as setas (para arrastá-lo). Ele é
      // renderizado por portal fora da árvore DOM do carrossel, então
      // stopPropagation no React não impede este listener de window — a
      // checagem do alvo é o que garante que arrastar não troque de questão.
      const target = event.target as HTMLElement | null;
      if (target?.closest?.("[data-shared-text-panel]")) return;
      // Mesmo motivo, agora que há formulários por cima da questão (denúncia,
      // comentário do fórum, anotador de PDF): eles também são portais, e sem
      // isto mover o cursor no texto trocaria a questão por baixo do diálogo.
      if (estaDigitando(target) || target?.closest?.("[role='dialog']")) return;

      if (event.key === "ArrowRight") irParaProxima();
      if (event.key === "ArrowLeft") irParaAnterior();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [resolucaoProxima, resolucaoAnterior]);

  // Atalhos do design: A–E marca a alternativa, Enter confirma.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (estaDigitando(target)) return;
      if (target?.closest?.("[data-shared-text-panel]")) return;
      if (target?.closest?.("[role='dialog']")) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const podeEscolher = resolucaoAtual.id === RESOLUCAO_VAZIA;
      if (!podeEscolher) return;

      const choices =
        resolucaoAtual.question?.choices?.filter((c) => c.description?.trim()) ?? [];

      const k = event.key.toLowerCase();
      if (k.length === 1 && k >= "a" && k <= "e") {
        const idx = k.charCodeAt(0) - 97;
        if (!choices[idx]) return;
        // Shift + letra elimina ou restaura a alternativa (tesoura).
        if (event.shiftKey) {
          toggleEliminada(resolucaoAtual, idx);
          return;
        }
        const qid = String(resolucaoAtual.question?.id ?? "");
        if ((eliminadas[qid] ?? []).includes(idx)) return;
        escolher(choices[idx], resolucaoAtual);
      }
      if (event.key === "Enter" && resolucaoAtual.answer && !loadingAnswer) {
        confirmar();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [resolucaoAtual, loadingAnswer, eliminadas]);

  // Touch navigation
  // Detect swipe gestures on mobile devices
  useEffect(() => {
    let touchStartX = 0;
    let touchStartY = 0;
    let isSwiping = false;

    const threshold = 80;

    // Um toque que começa dentro da folha do texto base é rolagem de leitura,
    // não navegação — senão ler o texto no celular trocaria de questão.
    const dentroDoPainel = (e: TouchEvent) =>
      Boolean((e.target as HTMLElement | null)?.closest?.("[data-shared-text-panel]"));

    const handleTouchStart = (e: TouchEvent) => {
      if (dentroDoPainel(e)) {
        isSwiping = false;
        touchStartX = NaN;
        return;
      }
      touchStartX = e.changedTouches[0].screenX;
      touchStartY = e.changedTouches[0].screenY;
      isSwiping = false;
      setSwiping(false);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (Number.isNaN(touchStartX)) return;
      const touchX = e.changedTouches[0].screenX;
      const touchY = e.changedTouches[0].screenY;
      const diffX = Math.abs(touchX - touchStartX);
      const diffY = Math.abs(touchY - touchStartY);

      if (diffX > threshold && diffX > diffY) {
        if (!isSwiping) {
          setSwiping(true); // só ativa sombra se for swipe horizontal
          isSwiping = true;
        }
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (Number.isNaN(touchStartX)) return;
      const touchEndX = e.changedTouches[0].screenX;
      const touchEndY = e.changedTouches[0].screenY;

      const diffX = touchStartX - touchEndX;
      const diffY = touchStartY - touchEndY;

      if (Math.abs(diffX) > threshold && Math.abs(diffX) > Math.abs(diffY)) {
        if (diffX > 0) {
          irParaProxima();
        } else {
          irParaAnterior();
        }
      }

      setSwiping(false); // remove sombra
    };

    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("touchend", handleTouchEnd);

    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [resolucaoProxima, resolucaoAnterior]);

  const returnAbsoluteIndex = (index: number) => {
    if (paginatedResult?.totalItens) {
      const limit = filterQuery?.limit ?? 1;
      return ((paginatedResult.paginaAtual ?? 0) - 1) * limit + index + 1;
    }
    return index + 1;
  };

  /** Situação visual de uma alternativa — espelha a lógica original do carrossel. */
  const situacaoDa = (
    alternativa: Alternativa.Alternativa,
    resolucao: Resolucao.ResolucaoQuestao,
  ): "correct" | "wrong" | "selected" | "default" => {
    if (resolucao.answerReviewed) {
      if (resolucao.correct !== undefined) {
        if (alternativa.correct) return "correct";
        if (resolucao.answer?.description === alternativa.description) return "wrong";
      }
      return "default";
    }

    const respondida = resolucao.id !== RESOLUCAO_VAZIA && resolucao.correct !== undefined;
    if (respondida && resolucao.answer) {
      if (alternativa.correct) return "correct";
      if (resolucao.answer?.description === alternativa.description) return "wrong";
      return "default";
    }

    return alternativa.description === resolucaoAtual.answer?.description
      ? "selected"
      : "default";
  };

  return (
    <>
      {isMobile && swiping && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.1)",
            zIndex: 999,
            pointerEvents: "none",
            transition: "opacity 0.2s ease-in-out",
          }}
        />
      )}

      <DefaultLayout>
        {/*
          Altura fixa SÓ quando o painel de PDF está aberto.

          Antes ela valia em todo desktop, e uma questão alta transbordava:
          medido com um enunciado longo, a caixa ficava nos 820px do
          `calc(100vh - 80px)` enquanto o conteúdo pedia 3103px. Como a caixa
          não tem `overflow`, o excedente era pintado por cima do rodapé, que
          começava em y=1014 — o sintoma relatado.

          O número existe pelo painel de PDF: ali as duas colunas rolam
          sozinhas (`overflow-y-auto` na questão, o visualizador na outra), e
          para isso cada uma precisa de uma altura definida. Sem o painel não
          há o que limitar — a página cresce com a questão e quem rola é a
          coluna de conteúdo do shell, deixando o rodapé no fim.
        */}
        <div
          className="flex gap-4"
          style={{ height: highlight && pdfVisible ? "calc(100vh - 80px)" : undefined }}
        >
          {/* pb-28 no mobile: a QuestionActionBar compacta é fixa no rodapé (portal)
              e sem a folga ela cobre o fim da questão. */}
          {!(isMobile && pdfVisible) && (
            <div
              className={
                highlight && pdfVisible && !isMobile
                  ? "flex-1 min-w-0 overflow-y-auto"
                  : isMobile
                    ? "w-full pb-28"
                    : "w-full"
              }
            >
              <Spin spinning={fetching} tip="Carregando..." size="large">
                <Tabs
                  defaultActiveKey={activeTab}
                  activeKey={activeTab}
                  tabPosition={"top"}
                  onChange={(tab) => {
                    resolucoes.map((resolucao) => {
                      if (resolucao.question?.id + "" === (tab as string)) {
                        marcarResolucaoAtual(resolucao);
                      }
                    });
                  }}
                  tabBarStyle={{ display: "none" }}
                >
                  {resolucoes?.map((resolucao, index) => {
                    const q = resolucao.question;
                    const choices =
                      q?.choices?.filter((c) => c.description?.trim()) ?? [];

                    const numero = resolucao.unitIndex ?? returnAbsoluteIndex(index);
                    const total = paginatedResult?.totalItens;
                    const progresso =
                      total && total > 0
                        ? Math.min(100, Math.max(0, (numero / total) * 100))
                        : 0;

                    const respondida =
                      resolucao.answerReviewed || resolucao.correct != null;

                    const correta = choices.find((c) => c.correct);
                    const letraDe = (alt?: Alternativa.Alternativa | null) => {
                      if (!alt) return null;
                      const i = choices.findIndex(
                        (c) => c.description === alt.description,
                      );
                      return i >= 0 ? String.fromCharCode(65 + i) : null;
                    };
                    const letraCorreta = letraDe(correta);
                    const letraEscolhida = letraDe(resolucao.answer);

                    const resultadoLinha = !respondida
                      ? null
                      : resolucao.answerReviewed && !resolucao.answer
                        ? letraCorreta
                          ? `Gabarito revelado · Resposta correta: ${letraCorreta}`
                          : "Gabarito revelado"
                        : resolucao.correct
                          ? `Você acertou · Resposta: ${letraCorreta ?? "—"}`
                          : `Você marcou ${letraEscolhida ?? "—"} · Resposta correta: ${letraCorreta ?? "—"}`;

                    const rationaleEscolhida =
                      resolucao.answer &&
                      !resolucao.answer.correct &&
                      choices.find(
                        (c) => c.description === resolucao.answer?.description,
                      )?.rationale;

                    const mostrarJustificativa =
                      respondida &&
                      Boolean(correta?.rationale?.trim() || rationaleEscolhida);

                    const sharedText = q?.sharedTextId
                      ? sharedTexts?.[q.sharedTextId]
                      : undefined;

                    const keywords = (q?.keyword ?? "")
                      .split(",")
                      .map((k: string) => k.trim())
                      .filter(Boolean);

                    // Linha de contexto do overlay mobile do fórum — lá a
                    // questão não está na tela junto.
                    const contextoQuestao = [
                      q?.subject?.name,
                      q?.testingAgency?.name,
                      q?.testingAgency?.year,
                    ]
                      .filter(Boolean)
                      .join(" · ");

                    const memorized = memorizedIds.has(String(q?.id ?? ""));
                    const memorizing = memorizingId === String(q?.id ?? "");

                    // Tesoura: depois de responder as marcações somem e o
                    // gabarito aparece normalmente.
                    const qid = String(q?.id ?? "");
                    const eliminadasQ = respondida ? [] : (eliminadas[qid] ?? []);
                    const riscadosQ = respondida ? [] : (riscados[qid] ?? []);
                    const totalTesoura = eliminadasQ.length + riscadosQ.length;
                    const rotuloTesoura =
                      riscadosQ.length > 0
                        ? `${totalTesoura} riscado${totalTesoura === 1 ? "" : "s"}`
                        : `${totalTesoura} eliminada${totalTesoura === 1 ? "" : "s"}`;
                    const segmentos = parseEnunciado(q?.statement ?? "");

                    const sidePanel = (
                      <aside className="flex w-full shrink-0 flex-col gap-4 lg:w-[300px]">
                        {/* Ferramentas */}
                        <section className="flex flex-col gap-3 rounded-card border border-edge bg-surface p-4 shadow-panel">
                          <h3 className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-muted">
                            Ferramentas
                          </h3>
                          <ToolRow
                            icon={<Eye size={18} aria-hidden />}
                            tile="bg-surface-muted text-ink-secondary"
                            title="Ver gabarito"
                            desc="Revela a resposta correta"
                            onClick={verGabarito}
                            disabled={respondida}
                            loading={loadingAnswer && !respondida}
                          />
                          <ToolRow
                            icon={
                              memorized ? (
                                <Check size={18} strokeWidth={3} aria-hidden />
                              ) : (
                                <Brain size={18} aria-hidden />
                              )
                            }
                            tile={
                              memorized
                                ? "bg-ok/[0.12] text-ok-ink"
                                : "bg-accent-purple/[0.14] text-accent-purple-ink"
                            }
                            title={memorized ? "Na revisão" : "Salvar para revisão"}
                            desc={
                              memorized
                                ? "Já está na sua revisão espaçada"
                                : "Adiciona à revisão espaçada"
                            }
                            onClick={() => onMemorize(q)}
                            disabled={memorized}
                            loading={memorizing}
                          />
                          {highlight && (
                            <ToolRow
                              icon={<FileText size={18} aria-hidden />}
                              tile="bg-accent-orange/[0.14] text-accent-orange-ink"
                              title={pdfVisible ? "Ocultar material em PDF" : "Ver material em PDF"}
                              desc={`Página ${highlight.page} · trecho vinculado`}
                              onClick={() => setPdfVisible((v) => !v)}
                            />
                          )}
                          {/* No desktop "Denunciar" fica no cabeçalho do card
                              (linha da banca), como no frame. O mobile não tem
                              aquele cabeçalho, então a ação vem para cá. */}
                          {isMobile && (
                            <ToolRow
                              icon={<Flag size={18} aria-hidden />}
                              tile="bg-warn/[0.12] text-warn-ink"
                              title="Denunciar questão"
                              desc="Gabarito errado, lei mudada, erro no texto"
                              onClick={() => denunciarQuestao(q, numero)}
                            />
                          )}
                        </section>

                        {/* Sobre a questão */}
                        <section className="flex flex-col gap-3.5 rounded-card border border-edge bg-surface p-5 shadow-panel">
                          <h3 className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-muted">
                            Sobre a questão
                          </h3>
                          <div className="flex items-start justify-between gap-3">
                            <span className="shrink-0 text-[13px] text-ink-secondary">
                              Matéria
                            </span>
                            <span
                              className="truncate text-[13px] font-semibold text-ink"
                              title={q?.subject?.name}
                            >
                              {q?.subject?.name ?? "—"}
                            </span>
                          </div>
                          <div className="flex items-start justify-between gap-3">
                            <span className="shrink-0 text-[13px] text-ink-secondary">
                              Banca / ano
                            </span>
                            <span className="text-right text-[13px] font-semibold text-ink">
                              {q?.testingAgency?.name ?? "—"} ·{" "}
                              {q?.testingAgency?.year ?? "—"}
                            </span>
                          </div>
                          {keywords.length > 0 && (
                            <div className="flex items-start justify-between gap-3">
                              <span className="shrink-0 text-[13px] text-ink-secondary">
                                Assuntos
                              </span>
                              <span className="flex flex-wrap justify-end gap-1.5">
                                {keywords.map((keyword: string, i: number) => (
                                  <span
                                    key={keyword}
                                    className={cx(
                                      "rounded-[5px] px-1.5 py-0.5 text-[11px] font-bold",
                                      TAG_TINTS[i % TAG_TINTS.length],
                                    )}
                                  >
                                    {keyword}
                                  </span>
                                ))}
                              </span>
                            </div>
                          )}
                        </section>

                        {/* Gerenciar (admin) */}
                        {isAdmin && (
                          <section className="flex flex-col gap-3 rounded-card border border-edge bg-surface p-4 shadow-panel">
                            <div className="flex items-center justify-between gap-2">
                              <h3 className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-muted">
                                Gerenciar
                              </h3>
                              {q?.disabled && (
                                <span className="rounded-full border border-danger/40 bg-danger/[0.12] px-2 py-0.5 text-[11px] font-bold text-danger-ink">
                                  DESATIVADA
                                </span>
                              )}
                            </div>
                            <ToolRow
                              icon={<Pencil size={18} aria-hidden />}
                              tile="bg-brand/[0.12] text-brand"
                              title="Alterar questão"
                              desc="Editar enunciado e alternativas"
                              onClick={() =>
                                navigate("/questao", { state: { questao: q } })
                              }
                            />
                            <ToolRow
                              icon={<FileText size={18} aria-hidden />}
                              tile="bg-surface-muted text-ink-secondary"
                              title={highlight ? "Editar vínculo PDF" : "Vincular PDF"}
                              desc={
                                highlight
                                  ? "Ajustar o trecho marcado na prova"
                                  : "Marcar o trecho desta questão na prova"
                              }
                              onClick={() => setAnnotatorOpen(true)}
                            />
                            <ToolRow
                              icon={
                                q?.disabled ? (
                                  <Check size={18} aria-hidden />
                                ) : (
                                  <Ban size={18} aria-hidden />
                                )
                              }
                              tile={
                                q?.disabled
                                  ? "bg-ok/[0.12] text-ok-ink"
                                  : "bg-danger/[0.10] text-danger-ink"
                              }
                              title={q?.disabled ? "Ativar questão" : "Desativar questão"}
                              desc={
                                q?.disabled
                                  ? "Volta a aparecer para os alunos"
                                  : "Some do banco para os alunos"
                              }
                              onClick={() => alternarAtivacao(q)}
                            />
                          </section>
                        )}
                      </aside>
                    );

                    return (
                      <TabPane
                        tab={`Questão-${index + 1}`}
                        key={q?.id + ""}
                        destroyInactiveTabPane
                        forceRender
                      >
                        {/* SÓ a aba ativa monta a barra fixa: com `forceRender`
                            todas as abas renderizam, e cada barra é um portal
                            fixo no rodapé — elas empilhavam e a de uma questão
                            inativa (ainda "Confirmar resposta") engolia todo
                            clique na base da tela, inclusive o Publicar do
                            fórum. */}
                        {isMobile && activeTab === qid && (
                          <QuestionActionBar
                            compact
                            navigating={fetching}
                            answering={loadingAnswer}
                            answered={respondida}
                            onPrevious={irParaAnterior}
                            onNext={irParaProxima}
                            onRevealAnswer={verGabarito}
                            onConfirm={confirmar}
                          />
                        )}

                        <div className="flex flex-col gap-5">
                          {/* Session bar */}
                          <div className="flex flex-col gap-3">
                            <div className="flex min-w-0 flex-col gap-1">
                              <span className="truncate text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-secondary">
                                Treino{q?.subject?.name ? ` · ${q.subject.name}` : ""}
                              </span>
                              <div className="flex items-baseline gap-2">
                                <h1 className="m-0 text-2xl font-bold leading-tight text-ink">
                                  Questão {numero}
                                </h1>
                                {total ? (
                                  <span className="text-[15px] font-medium text-ink-muted">
                                    de {total}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                            <div
                              className="h-1 overflow-hidden rounded-full bg-edge"
                              role="progressbar"
                              aria-valuemin={0}
                              aria-valuemax={total ?? 0}
                              aria-valuenow={numero}
                            >
                              <div
                                className="h-full rounded-full bg-brand transition-[width] duration-300"
                                style={{ width: `${progresso}%` }}
                              />
                            </div>
                          </div>

                          {/* Body: coluna principal (card + fórum) + painel lateral */}
                          <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
                            <div className="flex min-w-0 flex-1 flex-col gap-5">
                              {/* O cartão da questão É o `<Form>` do antd. Ele envolvia o
                                  `<Tabs>` inteiro — e, com ele, o fórum: como `CommentComposer`
                                  tem o seu próprio `<form onSubmit>`, ficava um formulário
                                  dentro do outro, HTML inválido que só não navegava porque o
                                  handler interno chama `preventDefault()`. Decisão 7 do design:
                                  quem estava no lugar errado era o fórum, não o composer. O
                                  formulário termina aqui, no cartão; o fórum é irmão dele. */}
                              <Form
                                form={form}
                                layout={"vertical"}
                                autoComplete={"off"}
                                className="overflow-hidden rounded-card border border-edge bg-surface shadow-panel"
                              >
                                <div className="flex flex-col gap-5 p-5 md:p-7">
                                  {/* Source row */}
                                  <div className="flex flex-wrap items-center gap-2.5">
                                    {(q?.testingAgency?.name || q?.testingAgency?.year) && (
                                      <span className="inline-flex items-center gap-1.5 rounded-[6px] bg-surface-muted px-2.5 py-1 text-xs font-semibold text-ink-secondary">
                                        <Landmark size={13} aria-hidden />
                                        {q?.testingAgency?.name}
                                        {q?.testingAgency?.year
                                          ? ` · ${q.testingAgency.year}`
                                          : ""}
                                      </span>
                                    )}
                                    {((q?.resolvedCount ?? 0) > 0 ||
                                      (q?.correctCount ?? 0) > 0) && (
                                      <span className="inline-flex items-center gap-2 rounded-full border border-edge bg-surface px-3 py-1 text-xs font-semibold text-ink-secondary">
                                        <History size={13} aria-hidden />
                                        Resolvida {q?.resolvedCount ?? 0}×
                                        <span
                                          aria-hidden
                                          className="h-3 w-px bg-edge"
                                        />
                                        <span className="inline-flex items-center gap-1 text-ok-ink">
                                          <CircleCheck size={13} aria-hidden />
                                          {q?.correctCount ?? 0} acerto
                                          {(q?.correctCount ?? 0) === 1 ? "" : "s"}
                                        </span>
                                      </span>
                                    )}
                                    {!isMobile && (
                                      <button
                                        type="button"
                                        onClick={() => denunciarQuestao(q, numero)}
                                        className={cx(
                                          "ml-auto inline-flex h-[30px] shrink-0 items-center gap-1.5",
                                          "rounded-[7px] px-2.5 text-[13px] font-medium text-ink-secondary",
                                          "transition-colors duration-150 motion-reduce:transition-none",
                                          "hover:bg-surface-hover hover:text-ink",
                                        )}
                                      >
                                        <Flag size={14} aria-hidden />
                                        Denunciar
                                      </button>
                                    )}
                                  </div>

                                  {/* Barra do texto base */}
                                  {q?.sharedTextId && sharedText && (
                                    <div className="flex items-center gap-3 rounded-chip border border-brand/30 bg-brand/[0.08] px-4 py-3">
                                      <BookOpenText
                                        size={18}
                                        className="shrink-0 text-brand"
                                        aria-hidden
                                      />
                                      <div className="min-w-0 flex-1">
                                        <p
                                          className="m-0 truncate text-[13px] font-semibold text-ink"
                                          title={sharedText.title}
                                        >
                                          {sharedText.title}
                                          {(resolucao.groupSize ?? 0) > 1 && (
                                            <span className="font-normal text-ink-secondary">
                                              {" "}
                                              · {resolucao.groupPos}/{resolucao.groupSize}
                                            </span>
                                          )}
                                        </p>
                                        <p className="m-0 truncate text-xs text-ink-secondary">
                                          {(resolucao.groupSize ?? 0) > 1
                                            ? `Com base no texto, responda às ${resolucao.groupSize} questões seguintes.`
                                            : "Com base no texto, responda à questão."}
                                        </p>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => abrirTexto(q.sharedTextId!)}
                                        className={cx(
                                          "inline-flex shrink-0 items-center gap-1.5 rounded-[7px] px-2.5 py-1.5 text-xs font-semibold",
                                          "transition-colors duration-150 motion-reduce:transition-none",
                                          textoAberto === q.sharedTextId
                                            ? "border border-brand/50 bg-surface text-brand"
                                            : "bg-brand text-brand-contrast hover:brightness-110",
                                          textoAberto !== q.sharedTextId &&
                                            !textosVistos.has(q.sharedTextId) &&
                                            "animate-pulse",
                                        )}
                                      >
                                        {textoAberto === q.sharedTextId ? (
                                          <PanelRightClose size={14} aria-hidden />
                                        ) : (
                                          <PanelRightOpen size={14} aria-hidden />
                                        )}
                                        {textoAberto === q.sharedTextId
                                          ? "Ocultar texto"
                                          : "Abrir texto"}
                                      </button>
                                    </div>
                                  )}

                                  {/* Questão desatualizada */}
                                  {q?.outdated && (
                                    <div className="flex items-start gap-3 rounded-chip border border-warn/40 bg-warn/[0.10] px-4 py-3">
                                      <TriangleAlert
                                        size={18}
                                        className="mt-0.5 shrink-0 text-warn-ink"
                                        aria-hidden
                                      />
                                      <div className="min-w-0">
                                        <p className="m-0 text-sm font-semibold text-warn-ink">
                                          Questão desatualizada — não deve ser considerada
                                          para fins de estudo.
                                        </p>
                                        <p className="m-0 mt-1 text-sm text-ink-secondary">
                                          {q.outdated}
                                        </p>
                                      </div>
                                    </div>
                                  )}

                                  {/* Enunciado — itens com numeral romano são riscáveis */}
                                  <div className="enunciado-md flex flex-col gap-2 rounded-chip bg-select/[0.08] px-5 py-4">
                                    {segmentos.map((seg, segIndex) =>
                                      seg.tipo === "md" ? (
                                        <MarkdownEditor
                                          key={segIndex}
                                          readOnly={true}
                                          value={seg.texto}
                                        />
                                      ) : (
                                        <div
                                          key={segIndex}
                                          className="group/riscavel flex items-center gap-2"
                                        >
                                          {/* O parágrafo inteiro acende no hover:
                                              é este bloco que a tesoura risca. */}
                                          <div
                                            className={cx(
                                              "flex min-w-0 flex-1 items-baseline gap-2.5 rounded-[8px] px-2 py-1.5",
                                              "transition-colors duration-150 motion-reduce:transition-none",
                                              !respondida &&
                                                !riscadosQ.includes(seg.indice) &&
                                                "group-hover/riscavel:bg-surface group-hover/riscavel:ring-1 group-hover/riscavel:ring-edge",
                                            )}
                                          >
                                            <span
                                              className={cx(
                                                "w-7 shrink-0 text-[15px] font-bold",
                                                riscadosQ.includes(seg.indice)
                                                  ? "text-ink-muted"
                                                  : "text-ink",
                                              )}
                                            >
                                              {seg.numeral}
                                            </span>
                                            <span
                                              className={cx(
                                                "min-w-0 text-[15px] leading-relaxed",
                                                riscadosQ.includes(seg.indice)
                                                  ? "text-ink-muted line-through"
                                                  : "text-ink",
                                              )}
                                            >
                                              {seg.texto}
                                            </span>
                                          </div>
                                          {!respondida && (
                                            <ScissorButton
                                              cut={riscadosQ.includes(seg.indice)}
                                              onClick={() =>
                                                toggleRiscado(qid, seg.indice)
                                              }
                                              cutLabel="Riscar item"
                                              restoreLabel="Restaurar item"
                                              size="sm"
                                              alwaysVisible={isMobile}
                                            />
                                          )}
                                        </div>
                                      ),
                                    )}
                                  </div>

                                  {/* Alternativas */}
                                  <div className="flex flex-col gap-2.5">
                                    {choices.map((alternativa, altIndex) => {
                                      const eliminada = eliminadasQ.includes(altIndex);
                                      const situacao = eliminada
                                        ? "default"
                                        : situacaoDa(alternativa, resolucao);
                                      // A tesoura some da última alternativa restante.
                                      const mostraTesoura =
                                        !respondida &&
                                        (eliminada ||
                                          eliminadasQ.length < choices.length - 1);
                                      return (
                                        <div
                                          key={altIndex}
                                          className="group/riscavel flex items-center gap-2.5"
                                        >
                                          <div className="min-w-0 flex-1">
                                            <ChoiceButton
                                              letter={String.fromCharCode(65 + altIndex)}
                                              label={alternativa.description ?? ""}
                                              situation={situacao}
                                              eliminated={eliminada}
                                              onClick={
                                                !eliminada &&
                                                (situacao === "default" ||
                                                  situacao === "selected")
                                                  ? () =>
                                                      escolher(alternativa, resolucao)
                                                  : undefined
                                              }
                                            />
                                          </div>
                                          {mostraTesoura ? (
                                            <ScissorButton
                                              cut={eliminada}
                                              onClick={() =>
                                                toggleEliminada(resolucao, altIndex)
                                              }
                                              cutLabel="Eliminar alternativa"
                                              restoreLabel="Restaurar alternativa"
                                              size={isMobile ? "lg" : "md"}
                                              alwaysVisible={isMobile}
                                            />
                                          ) : (
                                            !respondida && (
                                              <span
                                                aria-hidden
                                                className={cx(
                                                  "shrink-0",
                                                  isMobile ? "w-10" : "w-9",
                                                )}
                                              />
                                            )
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>

                                  {/* Contador da tesoura no mobile — no desktop
                                      ele vive no rodapé do card. */}
                                  {isMobile && totalTesoura > 0 && !respondida && (
                                    <div className="flex items-center gap-1.5 text-xs">
                                      <Scissors
                                        size={14}
                                        className="shrink-0 text-ink-secondary"
                                        aria-hidden
                                      />
                                      <span className="font-semibold text-ink-secondary">
                                        {rotuloTesoura}
                                      </span>
                                      <span aria-hidden className="text-ink-muted">
                                        ·
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => restaurarTudo(qid)}
                                        className="font-semibold text-brand"
                                      >
                                        Restaurar todas
                                      </button>
                                    </div>
                                  )}

                                  {/* Justificativa */}
                                  {mostrarJustificativa && (
                                    <div className="flex flex-col gap-3 rounded-card border border-edge bg-surface-muted p-5">
                                      <div className="flex items-center gap-3">
                                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] bg-ok/[0.12] text-ok-ink">
                                          <Lightbulb size={18} aria-hidden />
                                        </span>
                                        <div className="min-w-0">
                                          <p className="m-0 text-[15px] font-bold text-ink">
                                            Justificativa
                                          </p>
                                          {resultadoLinha && (
                                            <p className="m-0 text-xs font-medium text-ink-secondary">
                                              {resultadoLinha}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                      {correta?.rationale?.trim() && (
                                        <p className="m-0 text-[15px] leading-relaxed text-ink-secondary">
                                          {correta.rationale}
                                        </p>
                                      )}
                                      {rationaleEscolhida && (
                                        <p className="m-0 text-sm leading-relaxed text-ink-secondary">
                                          <span className="font-semibold text-ink">
                                            Sobre a alternativa {letraEscolhida}:
                                          </span>{" "}
                                          {rationaleEscolhida}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {!isMobile && (
                                  <QuestionActionBar
                                    navigating={fetching}
                                    answering={loadingAnswer}
                                    answered={respondida}
                                    onPrevious={irParaAnterior}
                                    onNext={irParaProxima}
                                    onRevealAnswer={verGabarito}
                                    onConfirm={confirmar}
                                    hint="A–E marca · Shift+letra risca · Enter confirma"
                                    scissorCount={totalTesoura}
                                    scissorLabel={rotuloTesoura}
                                    onRestoreAll={() => restaurarTudo(qid)}
                                  />
                                )}
                              </Form>

                              {/* Fórum da questão — irmão do card e **fora** do
                                  `<Form>` acima, logo abaixo dele (frame
                                  `tcgeJ`): embutido no desktop, ponto de
                                  entrada + overlay de página inteira no mobile.
                                  Só a questão da aba ativa monta, e só depois
                                  de respondida: sem isso o carrossel somaria
                                  uma busca de fórum por questão da página
                                  (decisão 4 do design). */}
                              {respondida && activeTab === qid && (
                                <QuestionForum
                                  questionId={qid}
                                  answered
                                  mobile={isMobile}
                                  questionLabel={`Questão ${numero}`}
                                  questionContext={contextoQuestao}
                                  currentUserName={usuario?.fullName ?? usuario?.userName}
                                  profile={usuario?.profile as Profile | undefined}
                                  onNavigate={navigate}
                                />
                              )}
                            </div>

                            {!(highlight && pdfVisible) && sidePanel}
                          </div>
                        </div>
                      </TabPane>
                    );
                  })}
                </Tabs>
              </Spin>
            </div>
          )}

          {highlight && pdfVisible && (
            <div
              className={
                isMobile ? "w-full h-full" : "border-l border-edge flex-shrink-0"
              }
              style={isMobile ? undefined : { width: "45%", minWidth: 340 }}
            >
              <PdfViewer
                fileKey={highlight.file_key ?? ""}
                page={highlight.page}
                rect={highlight.rect}
                reviewed={highlight.reviewed}
                onHide={() => setPdfVisible(false)}
                otherMarks={allPdfMarks.map((m) => ({ page: m.page, rect: m.rect }))}
              />
            </div>
          )}
        </div>

        {textoAberto && sharedTexts?.[textoAberto] && (
          <SharedTextPanel
            text={sharedTexts[textoAberto]}
            groupPosition={
              (resolucaoAtual.groupSize ?? 0) > 1
                ? {
                    index: resolucaoAtual.groupPos ?? 1,
                    total: resolucaoAtual.groupSize ?? 1,
                  }
                : undefined
            }
            mobile={isMobile}
            geometryRef={textoGeometriaRef}
            onClose={() => setTextoAberto(null)}
          />
        )}

        {annotatorOpen && (
          <Modal
            open
            onCancel={() => {
              setAnnotatorOpen(false);
              if (questionId)
                PdfHighlightService.getByQuestion(questionId)
                  .then((h) => setHighlight(h ?? null))
                  .catch(() => setHighlight(null));
            }}
            width="90vw"
            footer={null}
            title="Editar vínculo PDF"
            styles={{ body: { height: "80vh", padding: 0 } }}
          >
            <PdfAnnotator
              questionId={questionId}
              questionLabel={resolucaoAtual.question?.statement?.slice(0, 60)}
              onSave={() => {
                if (questionId)
                  PdfHighlightService.getByQuestion(questionId)
                    .then((h) => setHighlight(h ?? null))
                    .catch(() => {});
              }}
            />
          </Modal>
        )}

        {denuncia && (
          <ReportQuestionModal
            open
            questionId={denuncia.id}
            reference={denuncia.reference}
            onClose={() => setDenuncia(null)}
          />
        )}
      </DefaultLayout>
    </>
  );
}
